import { useContext, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useQuery } from "react-query";
import { GlobalQueries } from "hooks/useRefreshQuery";
import useAPI from "hooks/useAPI";
import HeaderLink from "./HeaderLink/HeaderLink";
import HeaderSearch from "./HeaderSearch/HeaderSearch";
import classes from "./Header.module.scss";
import CartDialog from "components/CartDialog/CartDialog";
import icons from "helpers/icons";
import Sitemap from "routes/sitemap";
import { useTranslation } from "react-i18next";
import AuthContext from "store/auth-context";
import { currencyFormat } from "helpers/functions";

const formatReceivable = (value, sign, currency) => {
    let amount;
    if (value === null || value === undefined || value === "" || !Number.isFinite(Number(value))) {
        amount = `${sign}0.000`;
    } else {
        amount = `${sign}${new Intl.NumberFormat("de-DE", { maximumFractionDigits: 2 }).format(Math.abs(Number(value)))}`;
    }

    return currency ? `${amount} ${currency}` : amount;
};

/**
 * Header of B2B
 *
 * @author Aleksandar Ječmenić <aleksandar.jecmenic@croonus.com>
 */

const Header = ({ setOpenModal, openCartDialog, setOpenCartDialog, setOpenCategoriesModal }) => {
    const { t } = useTranslation();
    const { user } = useContext(AuthContext);

    // State for search
    const [search, setSearch] = useState("");
    const { pathname } = useLocation();

    /**
     * Handle seacrh subit.
     *
     * @param {Event} e
     */
    // const onSearchClick = (e) => {
    //   if (search.trim() !== "" && search.trim().length > 2) {
    //     navigate(`/katalog/pretraga/` + encodeURIComponent(search));
    //     setSearch("");
    //     e.preventDefault();
    //   } else {
    //     toast.warning("Neophodno je da unesite bar 3 karaktera!");
    //     e.preventDefault();
    //   }
    // };

    const api = useAPI();

    // Load all cart items
    const { data: cart } = useQuery([GlobalQueries.Cart], () => api.list(`/cart`));
    const itemsInCart = cart?.items?.length;
    const cartTotal = currencyFormat(cart?.summary?.totals?.subtotal ?? 0, cart?.summary?.currency);

    const { data: sales } = useQuery([GlobalQueries.Sales], () => api.get(`/sales-officer`));

    const toggleSalesDialog = () => setOpenSalesDialog((prev) => !prev);

    const openCategoriesFromCart = () => {
        setOpenCartDialog(false);
        setOpenModal("sidebar");
        setOpenCategoriesModal(true);
    };

    const headerLinks = [
        { key: "cart", tour: "cart", onClick: () => {
            if (pathname === "/korpa/potvrda") return;
            setOpenModal("header");
            setOpenCategoriesModal(false);
            setOpenCartDialog(true);
        }, icon: icons.cart, alert: itemsInCart > 0 ? itemsInCart : null, text: cartTotal, title: t("cart-default-title") },
    ];

    const [openSalesDialog, setOpenSalesDialog] = useState(false);
    const salesCloseRef = useRef(null);

    useEffect(() => {
        if (!openSalesDialog) return;
        const previousFocus = document.activeElement;
        const closeOnEscape = (event) => {
            if (event.key === "Escape") setOpenSalesDialog(false);
        };

        salesCloseRef.current?.focus();
        document.addEventListener("keydown", closeOnEscape);
        return () => {
            document.removeEventListener("keydown", closeOnEscape);
            previousFocus?.focus();
        };
    }, [openSalesDialog, setOpenSalesDialog]);

    const REACT_APP_URL = localStorage.getItem("api");
    const cmsCurrency = user?.price_market?.currency || cart?.summary?.currency || localStorage.getItem("currency");
    const receivableCurrency = typeof cmsCurrency === "string" && !["undefined", "null"].includes(cmsCurrency.trim().toLowerCase()) ? cmsCurrency.trim().toUpperCase() : "";
    const receivableIn = formatReceivable(user?.company?.debt?.in, "+", receivableCurrency);
    const receivableOut = formatReceivable(user?.company?.debt?.out, "-", receivableCurrency);
    return (
        <>
            <header className={classes.wrapper}>
                <div className={classes.leftWrapper}>
                    <div className={classes.brandGroup}>
                        <Link to={"/pocetna"} className={classes.logo}>
                            <img src={`${REACT_APP_URL ? REACT_APP_URL : process.env.REACT_APP_URL}configuration/images/presentation/basic/logo`} alt="VOX" />
                        </Link>
                        {user?.company?.name && (
                            <div className={classes.companyGreeting}>
                                <span className={classes.welcome}>Dobrodošli</span>
                                <Link to={Sitemap.profile._} className={classes.companyLink} title={user.company.name}>
                                    {user.company.name}
                                </Link>
                            </div>
                        )}
                    </div>
                    <HeaderSearch search={search} setSearch={setSearch} translateKey={"global-header-search"} />
                    <div className={classes.searchAccessories}>
                        <Link to={Sitemap.static.analyticCard} className={classes.receivables} title="Analitička kartica" data-tour="receivables">
                            <span className={classes.receivableIn} title="Potraživanje u valuti" aria-label={`Potraživanje u valuti: ${receivableIn}`}>
                                {receivableIn}
                            </span>
                            <span className={classes.receivableOut} title="Potraživanje van valute" aria-label={`Potraživanje van valute: ${receivableOut}`}>
                                {receivableOut}
                            </span>
                        </Link>
                        <HeaderLink title={t("global-header-sale-title")} onClick={toggleSalesDialog} img={icons.suitcase} text="Prodaja" tour="sales" />
                    </div>
                </div>
                <Link to={Sitemap.static.analyticCard} className={`${classes.receivables} ${classes.mobileReceivables}`} title="Analitička kartica" data-tour="receivables">
                    <span className={classes.receivableIn} title="Potraživanje u valuti" aria-label={`Potraživanje u valuti: ${receivableIn}`}>
                        {receivableIn}
                    </span>
                    <span className={classes.receivableOut} title="Potraživanje van valute" aria-label={`Potraživanje van valute: ${receivableOut}`}>
                        {receivableOut}
                    </span>
                </Link>
                <div className={`${classes.headerLinks} ${classes.linksDesktop}`}>
                    {headerLinks.map((link) => (
                        <HeaderLink title={link.title} key={link.key} to={link.to} onClick={link.onClick} img={link.icon} alert={link.alert} text={link.text} tour={link.tour} />
                    ))}
                </div>
                <div className={`${classes.headerLinks} ${classes.linksMobile}`}>
                    {headerLinks.map((link) => (
                        <HeaderLink title={link.title} key={link.key} to={link.to} onClick={link.onClick} img={link.icon} alert={link.alert} text={link.text} tour={link.tour} />
                    ))}
                </div>
            </header>
            <CartDialog openDialog={openCartDialog} setOpenDialog={setOpenCartDialog} translateKey={"global-header-cart"} onOpenCategories={openCategoriesFromCart} />
            {openSalesDialog && (
                <div
                    className={classes.salesDialog}
                    onMouseDown={(event) => {
                        if (event.target === event.currentTarget) setOpenSalesDialog(false);
                    }}
                >
                    <div className={classes.salesDialogBox} role="dialog" aria-modal="true" aria-labelledby="sales-dialog-title">
                        <div className={classes.salesDialogHeader}>
                            <h2 id="sales-dialog-title" className={classes.salesDialogTitle}>
                                {t("global-header-sale-title")}
                            </h2>
                            <button ref={salesCloseRef} type="button" className={classes.salesDialogClose} onClick={() => setOpenSalesDialog(false)} aria-label="Zatvori">
                                <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                                    <path d="M5 5 15 15M15 5 5 15" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                                </svg>
                            </button>
                        </div>
                        {sales?.length > 0 ? (
                            <div className={classes.salesDialogTableWrap}>
                                <table className={classes.tbl}>
                                    <colgroup>
                                        <col span={1} style={{ width: "33.33%" }} />
                                        <col span={1} style={{ width: "33.33%" }} />
                                        <col span={1} style={{ width: "33.33%" }} />
                                    </colgroup>
                                    <thead>
                                        <tr>
                                            <th>{t("global-header-sale-table_th-full_name")}</th>
                                            <th>{t("global-header-sale-table_th-phone")}</th>
                                            <th>{t("global-header-sale-table_th-email")}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sales?.map((item, index) => (
                                            <tr key={item.id ?? index}>
                                                <td>
                                                    {item.first_name} {item?.last_name}
                                                </td>
                                                <td>
                                                    <a href={`tel:${item.phone}`}>{item.phone}</a>
                                                </td>
                                                <td>
                                                    <a href={`mailto:${item.email}`}>{item.email}</a>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className={classes.salesDialogNoSales}>
                                <span className={classes.salesDialogEmptyIcon} aria-hidden="true">
                                    <svg viewBox="0 0 32 32" fill="none">
                                        <circle cx="16" cy="11" r="4.5" stroke="currentColor" strokeWidth="1.6" />
                                        <path d="M7 25c.8-5 4.1-7.5 9-7.5s8.2 2.5 9 7.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                                    </svg>
                                </span>
                                <p className={classes.salesDialogNoSalesTitle}>{t("global-header-sale-messages-no_sales_assigned")}</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default Header;
